import { defineStore } from 'pinia'
import { markRaw, toRaw, type Ref, createApp } from 'vue'
import debounce from 'lodash/debounce'
import { useEventsStore } from '@/stores/apps/events'
import { useAuthStore } from '@/stores/auth'
import Marker from "@/views/cluedo_ai/Marker.vue"
import {
  FALLBACK_MAP_CENTER,
  getOrganizationInitialCenter,
  isFallbackMapCenter,
  resolveMapCenter,
} from '@/utils/mapCenter'

export interface MapMarker {
  id: string | number
  latitude: number
  longitude: number
  title: string
  zIndex?: number
  color?: string
  radius?: number
  isDraggable?: boolean
  customClass?: any
  infoWindow?: any
}

interface MapState {
  apiKey: string
  center: { lat: number; lng: number }
  zoom: number
  mapId: string
  markers: MapMarker[]
  mapInstance: google.maps.Map | null
  markerInstances: { [key: string]: {
    marker: google.maps.AdvancedMarkerElement,
    circle?: google.maps.Circle,
    pin: google.maps.marker.PinElement
  }}
  activeMarkerId: string | null
  initMarker: MapMarker | null
  finishMarker: MapMarker | null
  mapType: string
  mapControls: {
    clickableIcons: boolean
    disableDefaultUI: boolean
    zoomControl: boolean
    mapTypeControl: boolean
    scaleControl: boolean
    streetViewControl: boolean
    rotateControl: boolean
    fullscreenControl: boolean
    itemToShow: null | Array<any>
  }
  playerPositions: {
    id: string
    latitude: number
    longitude: number
  }[]
  monitorPositions: {
    monitor_id: string | number
    monitor_name?: string
    latitude: number
    longitude: number
  }[]
  paintPlayerPositions: boolean,
  objectNear: string | number | null,
  itemSelected: string | number | null
}

function resolveOrganizationForMapCenter() {
  const authStore = useAuthStore()
  const authOrg = (authStore.full_user as any)?.organization
  if (getOrganizationInitialCenter(authOrg)) {
    return authOrg
  }
  const eventsStore = useEventsStore()
  const eventOrg = (eventsStore.eventSelected as any)?.organization
  if (getOrganizationInitialCenter(eventOrg)) {
    return eventOrg
  }
  return authOrg ?? eventOrg ?? null
}

export const useMapStore = defineStore('map', {
  state: (): MapState => ({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    center: { ...FALLBACK_MAP_CENTER },
    zoom: 15,
    mapId: "4504f8b37365c3d0",
    markers: [],
    mapInstance: null,
    markerInstances: {},
    activeMarkerId: null,
    initMarker: null,
    finishMarker: null,
    mapType: 'satellite',
    mapControls: {
      clickableIcons: true,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      fullscreenControl: true
    },
    playerPositions: [],
    monitorPositions: [],
    paintPlayerPositions: false,
    objectNear: null,
    itemSelected: null
  }),

  getters: {
    mapConfig: (state) => {
      // Only fall back to organization when center is still the hardcoded default.
      // Once setCenter / setCenterFromPreferred runs (event start point, etc.), that wins.
      let center = state.center
      if (isFallbackMapCenter(center)) {
        const orgCenter = getOrganizationInitialCenter(resolveOrganizationForMapCenter())
        if (orgCenter) center = orgCenter
      }
      return {
        apiKey: state.apiKey,
        center,
        zoom: state.zoom,
        mapId: state.mapId,
        mapType: state.mapType,
        mapControls: state.mapControls
      }
    },

    activeMarkers: (state) => state.markers,

    markerElements: (state) => state.markerInstances,

    getMarkerById: (state) => (id: string | number) => 
      state.markers.find(marker => marker.id === id)
  },

  actions: {
    
    updatePlayerPositions(data:any){
      const teamId = data?.team_id
      if (teamId == null) {
        return
      }
      const positionDataIndex = this.playerPositions.findIndex(
        e => String(e.team_id) === String(teamId)
      )
      const new_data = {
        ...data,
        lastUpdate: new Date()
      }
      if (positionDataIndex >= 0) {
        const next = this.playerPositions.slice()
        next[positionDataIndex] = new_data
        this.playerPositions = next
      } else {
        this.playerPositions = [...this.playerPositions, new_data]
      }
    },
    updateMonitorPositions(data: any) {
      const monitorId = data.monitor_id
      if (monitorId == null) {
        return
      }
      const positionDataIndex = this.monitorPositions.findIndex(
        e => String(e.monitor_id) === String(monitorId)
      )
      const newData = {
        ...data,
        lastUpdate: new Date()
      }
      if (positionDataIndex >= 0) {
        const next = this.monitorPositions.slice()
        next[positionDataIndex] = newData
        this.monitorPositions = next
      } else {
        this.monitorPositions = [...this.monitorPositions, newData]
      }
    },
    async waitForMapReady() {
      // Wait for map instance to be available
      if (!this.mapInstance) {
        return new Promise((resolve) => {
          const checkMap = setInterval(() => {
            if (this.mapInstance) {
              clearInterval(checkMap)
              resolve(true)
            }
          }, 100)
        })
      }
      return Promise.resolve(true)
    },

    setMapInstance(map: google.maps.Map) {
      // Apply org default only while still on hardcoded fallback — never overwrite event coords.
      if (isFallbackMapCenter(this.center)) {
        const orgCenter = getOrganizationInitialCenter(resolveOrganizationForMapCenter())
        if (orgCenter) {
          this.center = orgCenter
        }
      }
      map.setOptions({
        mapId: this.mapId,
        center: this.center,
        zoom: this.zoom,
        mapTypeId: this.mapType,
        ...this.mapControls
      });
      
      // Never let Vue deeply proxy the Google Map — it freezes the tab.
      this.mapInstance = markRaw(map);
      this.initializeMarkers();
    },

    async initializeMarkers() {
      if (!this.mapInstance) return

      // Clear existing markers
      this.clearMarkerInstances()

      // Create new markers
      for (const marker of this.markers) {
        await this.createMarkerInstance(marker)
      }
    },

    /** AdvancedMarkerElement has no setMap(); detach via .map = null on the raw instance. */
    detachAdvancedMarker(marker?: google.maps.AdvancedMarkerElement | null) {
      if (!marker) return
      toRaw(marker).map = null
    },

    parseMarkerPosition(position: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral) {
      const latitude = typeof (position as google.maps.LatLng).lat === 'function'
        ? (position as google.maps.LatLng).lat()
        : Number((position as google.maps.LatLngLiteral).lat)
      const longitude = typeof (position as google.maps.LatLng).lng === 'function'
        ? (position as google.maps.LatLng).lng()
        : Number((position as google.maps.LatLngLiteral).lng)
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
      return { lat: latitude, lng: longitude }
    },

    async createMarkerInstance(markerData: MapMarker) {
      const map = this.mapInstance ? toRaw(this.mapInstance) : null
      if (!map) return

      // Replace any existing Google instance for this id.
      await this.deleteMarkerInstance(markerData.id)
      
      // Validate coordinates (don't use !latitude — 0 is valid on the equator/meridian)
      const lat = Number(markerData.latitude)
      const lng = Number(markerData.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.error("Invalid coordinates:", markerData);
        return;
      }

      const { InfoWindow } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
      // Bail if map was torn down or this id was recreated while we awaited libraries.
      if (!this.mapInstance || this.markerInstances[markerData.id]) {
        return
      }
      const infoWindow = new InfoWindow;
      
      let markerContent;
      if (markerData.customClass) 
      {
        if (markerData.customClass instanceof HTMLElement) {
          markerContent = markerData.customClass
        } else {
        // If customClass is provided, use custom DOM element
        const markerElement = document.createElement('div');
        if(typeof markerData.customClass == 'object')
        {
          const element = createApp(Marker, { typeChallenge: markerData.customClass.type, color: markerData.customClass.color}); 
          element.mount(markerElement);
        }
        else if (markerData.customClass == 'circle-player' || markerData.customClass == 'circle-monitor')
        {
          markerElement.style.display = 'flex';
          markerElement.style.flexDirection = 'column';
          markerElement.style.alignItems = 'center';
  
          // Create the text (above the circle)
          const textP = document.createElement("span");
          textP.textContent = markerData.title;
          textP.style.background = 'white';
          textP.style.padding = '2px 6px';
          textP.style.borderRadius = '4px';
          textP.style.fontSize = '12px';
          textP.style.marginBottom = '2px';
          textP.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
          textP.style.whiteSpace = 'nowrap';
  
          // Create the blue circle
          const circleDiv = document.createElement("div");
          circleDiv.className = markerData.customClass;
  
          // Add text and circle to the container
          markerElement.appendChild(textP);
          markerElement.appendChild(circleDiv);
        }
        else
        {
          markerElement.className = markerData.customClass;
        }
        markerContent = markerElement;
        }
      }
      else 
      {
        // If no customClass, use PinElement
        const pin = new PinElement({
          background: markerData.color || "#FF0000",
          borderColor: "#FFF",
          glyphColor: "#FFF"
        });
        markerContent = pin.element;
      }
      
      // Create off-map first, claim the slot with markRaw, then attach — avoids
      // orphan pins and Vue proxying Google objects (which freezes the tab).
      const marker = markRaw(new AdvancedMarkerElement({
        position: { lat: markerData.latitude, lng: markerData.longitude },
        title: markerData.title,
        gmpDraggable: markerData.isDraggable ?? false,
        gmpClickable: Boolean(markerData.infoWindow),
        content: markerContent,
        zIndex: markerData.zIndex ?? 10,
      }))

      let circle: google.maps.Circle | undefined
      if (markerData.radius) {
        circle = markRaw(new google.maps.Circle({
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.35,
          radius: markerData.radius,
          center: { lat, lng },
        }))
      }
      
      if (circle) {
        marker.addListener("drag", () => {
          const pos = this.parseMarkerPosition(marker.position as google.maps.LatLngLiteral)
          if (pos) circle.setCenter(pos)
        });
      }
      
      if (markerData?.infoWindow) {
        marker.addEventListener('gmp-click', () => {
          infoWindow.close();
          infoWindow.setContent(marker.title);
          infoWindow.open(marker.map, marker);
        });
      }

      marker.addListener('dragend', () => {
        const position = marker.position
        if (!position) return
        const parsed = this.parseMarkerPosition(position as google.maps.LatLngLiteral)
        if (!parsed) return
        if (circle) {
          circle.setCenter(parsed)
        }
        this.updateMarker(markerData.id, { latitude: parsed.lat, longitude: parsed.lng })
      })

      if (this.markerInstances[markerData.id]) {
        this.detachAdvancedMarker(marker)
        return
      }

      this.markerInstances[markerData.id] = markRaw({
        marker,
        circle,
        pin: markRaw(markerContent as object),
      })
      marker.map = map
      if (circle) {
        circle.setMap(map)
      }
      return marker;
    },

    async createTeamMarker(data:any)
    {
      const teamName = data.team.name_id + " - " + data.team.name;
      console.log("teamName", teamName)
      const circleDiv = document.createElement("div");
      const textP = document.createElement("p");
      const globalDiv = document.createElement("div");
      circleDiv.className = "circle-player";
      globalDiv.className = "player-content";
      textP.className="player-name"
      textP.textContent = teamName;
      globalDiv.appendChild(textP);
      globalDiv.appendChild(circleDiv);
      
      const marker = await this.createMarkerInstance({
        id: 'player-'+data.team.id,
        latitude: data.latitude,
        longitude: data.longitude,
        title: teamName,
        customClass: globalDiv,
      })

      if (marker) {
        marker.addListener("click", () => {
          const infowindow = new google.maps.InfoWindow({
            content: "Visto hace " + String(Math.round((new Date() - data.lastUpdate)/1000)) + " s",
          });
          infowindow.open(marker.map, marker);
        });
      }
      
    },

    updateTeamMarker(data:any)
    {
      const teamId = data.team?.id ?? data.team_id
      if (teamId == null) {
        console.error("updateTeamMarker: missing team id", data)
        return
      }
      const markerInstance = this.markerInstances['player-'+teamId];
      if (!markerInstance?.marker) {
        return
      }
      
      const lat = Number(data.latitude);
      const lng = Number(data.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.error("Invalid coordinates:", data);
        return;
      }
      
      google.maps.event.clearInstanceListeners(markerInstance.marker);
      
      // Create a proper LatLng object with validated coordinates
      const position = new google.maps.LatLng(lat, lng);
      markerInstance.marker.position = position;
      
      markerInstance.marker.addListener("click", () => {
          const infowindow = new google.maps.InfoWindow({
              content: "Visto hace " + String(Math.round((new Date() - data.lastUpdate)/1000)) + " s",
          });
          infowindow.open(markerInstance.marker.map, markerInstance.marker);
      });
    },
    
    async updatePlayerPosMap(){
      const eventStore = useEventsStore();
      for (const e of this.playerPositions) {
        const playerMakerIndex = this.markers.findIndex(findElement => findElement.id == 'player-'+e.team_id)
        e.team = eventStore.teams.find(team => team.id == e.team_id);
        if (playerMakerIndex == - 1) {
          await this.createTeamMarker(e);
        } else {
          await this.updateTeamMarker(e);
        }
      }
    },

    clearMarkerInstances() {
      Object.values(this.markerInstances).forEach(({ marker, circle }) => {
        this.detachAdvancedMarker(marker)
        circle?.setMap(null)
      })
      this.markerInstances = {}
    },

    setCenter(center: { lat: number; lng: number }) {
      this.center = center
      this.mapInstance?.setCenter(center)
    },

    /**
     * Center the map on preferred coords when valid; otherwise organization initial position.
     * Only updates when the resolved center differs, to avoid redundant Google Maps work.
     */
    setCenterFromPreferred(lat?: unknown, lng?: unknown) {
      const center = resolveMapCenter(
        { lat, lng },
        resolveOrganizationForMapCenter()
      )
      this.setCenter(center)
      return center
    },
    
    setZoom(zoom: number) {
      this.zoom = zoom
      this.mapInstance?.setZoom(zoom)
    },

    async addMarker(marker: MapMarker) {
      // Check if marker with this ID already exists
      if (!this.markers.some(m => m.id === marker.id)) {
        this.markers.push(marker)
      }
    },

    /** Replace/append many markers in one reactive update (avoids N watchers / API saves). */
    async addMarkersBatch(markers: MapMarker[]) {
      const byId = new Map(this.markers.map(m => [String(m.id), m]))
      for (const marker of markers) {
        byId.set(String(marker.id), marker)
      }
      this.markers = Array.from(byId.values())
      // Always recreate Google instances — updateMarkerInstance only moves position and
      // cannot apply customClass / isDraggable (stale activity-form pins looked like challenges).
      if (this.mapInstance) {
        for (const marker of this.markers) {
          await this.createMarkerInstance(marker)
        }
      }
    },

    fitBoundsToMarkers(padding: number = 64) {
      if (!this.mapInstance || this.markers.length === 0) return
      const bounds = new google.maps.LatLngBounds()
      let hasPoint = false
      for (const marker of this.markers) {
        const lat = Number(marker.latitude)
        const lng = Number(marker.longitude)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
        if (lat === 0 && lng === 0) continue
        bounds.extend({ lat, lng })
        hasPoint = true
      }
      if (hasPoint) {
        this.mapInstance.fitBounds(bounds, padding)
        const center = this.mapInstance.getCenter()
        if (center) {
          this.center = { lat: center.lat(), lng: center.lng() }
        }
      }
    },


    async updateMarker(id: string | number, updates: Partial<MapMarker>) {
      const index = this.markers.findIndex(m => m.id === id)
      if (index !== -1) {
        // Update the marker data in the array
        this.markers[index] = { ...this.markers[index], ...updates }
        
        // Update existing marker instance instead of creating a new one
        if (this.mapInstance && this.markerInstances[id]) {
          const position = { lat: this.markers[index].latitude, lng: this.markers[index].longitude }
          const markerInstance = this.markerInstances[id]
          
          // Update marker position
          markerInstance.marker.position = position
          
          // Update circle position if it exists
          if (markerInstance.circle) {
            markerInstance.circle.setCenter(position)
          }
        }
      }
    },

    async updateMarkerInstance(id: string | number, updates: Partial<MapMarker>) {
      const index = this.markers.findIndex(m => m.id === id)
      if (index !== -1) {
        if (this.mapInstance && this.markerInstances[id]) {
          const position = { lat: this.markers[index].latitude, lng: this.markers[index].longitude }
          const markerInstance = this.markerInstances[id]
          
          // Update marker position
          markerInstance.marker.position = position
          
          // Update circle position if it exists
          if (markerInstance.circle) {
            markerInstance.circle.setCenter(position)
          }
        }
      }
    },

    async deleteMarkerInstance(id: string | number) {
      if (this.markerInstances[id]) {
        this.detachAdvancedMarker(this.markerInstances[id].marker)
        this.markerInstances[id].circle?.setMap(null)
        delete this.markerInstances[id]
      }
    },
    zoomMarker(id: string | number)
    {   
      const object = this.markers.find(m => m.id === id)
      if( object && object.latitude != 0)
      {
        this.mapInstance?.setCenter({lat: object.latitude, lng: object.longitude});
        this.mapInstance?.setZoom(18)
      }
    },

    removeMarker(id: string | number) {
      void this.deleteMarkerInstance(id)
      this.markers = this.markers.filter(m => m.id !== id)
    },

    clearMarkers() {
      this.clearMarkerInstances()
      this.markers = []
    },

    handleMapClick(position: { lat: number, lng: number }) {
      // Handle map clicks here if needed
      console.log('Map clicked at:', position)
    },

    deleteMarker(id: string | number) {
      void this.deleteMarkerInstance(id)
      this.markers = this.markers.filter(m => m.id !== id)
    },

    checkMarker(id: string | number)
    {
      if(this.markerInstances[id])
      {
        return true
      }
      return false
    },
    
    placeMarker(id: string | number, title: string, pos: { lat: number, lng: number }, color: string) {
      // Always drop prior data/instance so re-placing the same id still gets a Google pin
      // (Map.vue only watches id-list changes and would skip an unchanged id string).
      this.deleteMarker(id)
      const isInit = String(id) === 'initMarker'
      const isFinish = String(id) === 'finishMarker'
      void this.addMarker({
        id,
        latitude: pos.lat,
        longitude: pos.lng,
        title,
        color,
        radius: 0,
        isDraggable: true,
        infoWindow: title,
        customClass: (isInit || isFinish)
          ? { type: 'flag', color: isInit ? 'green' : 'red' }
          : undefined,
      })
      this.activeMarkerId = id.toString()
      const data = this.markers.find(m => m.id === id)
      if (data && this.mapInstance) {
        void this.createMarkerInstance(data)
      }
    },

    cleanup() {
      this.clearMarkerInstances()
      this.markers = []
      this.mapInstance = null
    },

    growMarker: debounce(async function(id: string | number) {
      const markerInstance = this.markerInstances[id]
      if (!markerInstance?.pin) return

      const pinEl = (markerInstance.pin as any).element ?? markerInstance.pin
      if (pinEl?.style) {
        pinEl.style.transform = 'scale(1.3)'
        pinEl.style.transition = 'transform 0.2s ease-in-out'
      }
      
      if (markerInstance.circle) {
        const currentRadius = markerInstance.circle.getRadius()
        markerInstance.circle.setRadius(currentRadius * 1.3)
      }
    }, 50),

    shrinkMarker: debounce(async function(id: string | number) {
      const markerInstance = this.markerInstances[id]
      if (!markerInstance?.pin) return

      const pinEl = (markerInstance.pin as any).element ?? markerInstance.pin
      if (pinEl?.style) {
        pinEl.style.transform = 'scale(1)'
        pinEl.style.transition = 'transform 0.2s ease-in-out'
      }
      
      if (markerInstance.circle) {
        const currentRadius = markerInstance.circle.getRadius()
        markerInstance.circle.setRadius(currentRadius / 1.3)
      }
    }, 50),

    setMapType(type: string | null = null) {
      if (!this.mapInstance) return

      if (type) {
        this.mapType = type
        this.mapInstance.setMapTypeId(type)
      } else {
        // Toggle between satellite and roadmap
        const newType = this.mapType === 'satellite' ? 'roadmap' : 'satellite'
        this.mapType = newType
        this.mapInstance.setMapTypeId(newType)
      }

      // Always ensure mapId is set for roadmap type
      if (this.mapType === 'roadmap') {
        this.mapInstance.mapId = this.mapId
      } else {
        // For satellite view, we still need the mapId for markers
        this.mapInstance.mapId = this.mapId
      }

      console.log('Map type changed to:', this.mapType)
    },

    setMapControls(controls: Partial<MapState['mapControls']>) {
      console.log("controls", controls)
      this.mapControls = { ...this.mapControls, ...controls }
      if (this.mapInstance) {
        Object.entries(controls).forEach(([key, value]) => {
          // @ts-ignore - Google Maps types don't cover all options
          this.mapInstance?.set(key, value)
        })
      }
    }
  }
})
